import { clearGuestSessionQueryKey } from '@aline/common';
import { params, routes } from '@aline/routes';

const CONNECTION_PARAM = params.externalToolkitConnection;
const COMPLETION_PROOF_PATTERN = /^[A-Za-z0-9_-]{43}$/;

type ExternalToolkitConnectionStatus =
  (typeof CONNECTION_PARAM.status)[keyof typeof CONNECTION_PARAM.status];

interface ExternalToolkitCompletionCallback {
  readonly completionProof: string | null;
  readonly hasCompletionState: boolean;
  readonly hasSessionUri: boolean;
  readonly kind: 'completion';
  readonly shouldClearGuestSession: boolean;
  readonly status: ExternalToolkitConnectionStatus | null;
}

interface ExternalToolkitFallbackCallback {
  readonly kind: 'fallback';
  readonly shouldClearGuestSession: boolean;
}

export type ExternalToolkitCallback =
  | ExternalToolkitCompletionCallback
  | ExternalToolkitFallbackCallback;

/** Capture one-shot callback input from the URL at boot. */
export function captureExternalToolkitCallback(): ExternalToolkitCallback | null {
  if (window.location.pathname === routes.ApiIntegrationsComposioCompleteAuth) {
    const search = new URLSearchParams(window.location.search);
    const shouldClearGuestSession = getOnlyValue(search, clearGuestSessionQueryKey) === '1';
    return { kind: 'fallback', shouldClearGuestSession };
  }
  if (window.location.pathname === routes.ApiLoginCallback)
    return { kind: 'fallback', shouldClearGuestSession: false };

  if (window.location.pathname !== routes.ExternalToolkitConnected) return null;

  const search = new URLSearchParams(window.location.search);

  return {
    completionProof: getCompletionProof(search),
    hasCompletionState: search.has(CONNECTION_PARAM.completionState),
    hasSessionUri: search.has(CONNECTION_PARAM.sessionUri),
    kind: 'completion',
    shouldClearGuestSession: getOnlyValue(search, clearGuestSessionQueryKey) === '1',
    status: getConnectionStatus(search),
  };
}

function getConnectionStatus(search: URLSearchParams): ExternalToolkitConnectionStatus | null {
  const value = getOnlyValue(search, CONNECTION_PARAM.key);
  switch (value) {
    case CONNECTION_PARAM.status.authenticationRequired:
    case CONNECTION_PARAM.status.error:
    case CONNECTION_PARAM.status.expired:
    case CONNECTION_PARAM.status.success:
      return value;
    default:
      return null;
  }
}

function getCompletionProof(search: URLSearchParams): string | null {
  const value = getOnlyValue(search, CONNECTION_PARAM.completionProof);
  return value && COMPLETION_PROOF_PATTERN.test(value) ? value : null;
}

function getOnlyValue(search: URLSearchParams, key: string): string | null {
  const values = search.getAll(key);
  if (values.length !== 1) return null;
  return values[0] ?? null;
}
